import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useQueryClient } from "@tanstack/react-query";
import { tokenRequest } from "@/lib/msalConfig";
import {
  cacheBackendToken,
  getBackendToken,
} from "@/lib/tokenCache";
import { BACKEND_URL } from "@/lib/api/client";
import { TEST_AUTH_KEY } from "@/lib/testAuth";
import { fireDashboardPrefetches } from "@/hooks/useDashboardPrefetch";
import type { AuthUser, UseAuthReturn } from "@/types/auth";
import { AuthContext } from "@/hooks/authContext";

const EXCHANGE_TIMEOUT_MS = 10_000;

function getTestAuth(): {
  user: AuthUser;
  accessToken: string;
  backendToken: string;
} | null {
  try {
    const raw = localStorage.getItem(TEST_AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Parse basic user info from the backend JWT (sub, email, tenant_id). */
function parseUserFromJwt(token: string): AuthUser | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      id: payload.sub || "",
      email: payload.email || "",
      name: payload.email || "",
      tenantId: payload.tenant_id || undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Provider that runs all auth side-effects exactly once.
 * Mount this high in the tree (inside MsalProvider + QueryClientProvider).
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const value = useAuthInternal();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Internal hook: the actual auth logic (only called once by AuthProvider)
// ---------------------------------------------------------------------------

function useAuthInternal(): UseAuthReturn {
  // E2E test bypass: if __PLAYWRIGHT_AUTH__ is in localStorage, skip MSAL
  const testAuth = getTestAuth();
  const { instance, accounts, inProgress } = useMsal();
  const queryClient = useQueryClient();
  const account = useAccount(accounts[0] ?? null);
  const [accessToken, setAccessToken] = useState<string | null>(
    testAuth?.accessToken ?? null
  );
  const [backendToken, setBackendToken] = useState<string | null>(
    testAuth?.backendToken ?? getBackendToken()
  );
  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [error] = useState<string | null>(null);
  const [isExchanging, setIsExchanging] = useState(
    () => !testAuth && accounts.length > 0 && getBackendToken() === null,
  );
  const exchangeStarted = useRef(false);
  const prefetchFired = useRef(false);

  // Authenticated if MSAL has an account OR we have a valid backend token
  // (backend OAuth flow bypasses MSAL, so accounts may be empty).
  const hasMsalSession = accounts.length > 0;
  const isAuthenticated = testAuth ? true : (hasMsalSession || backendToken !== null);
  const isLoading = testAuth ? false : inProgress !== InteractionStatus.None;

  // Acquire Graph token silently when MSAL account is available
  useEffect(() => {
    if (!account) return;

    instance
      .acquireTokenSilent({ ...tokenRequest, account })
      .then((response) => {
        setAccessToken(response.accessToken);
      })
      .catch(() => {
        // Silent acquisition failed -- not critical when backend holds
        // refresh tokens and proxies Graph calls.
      });
  }, [account, instance]);

  // Exchange Graph token for backend token (only needed for MSAL-based login)
  useEffect(() => {
    if (!accessToken || backendToken || exchangeStarted.current) return;

    if (!BACKEND_URL) return;

    exchangeStarted.current = true;

    fetch(`${BACKEND_URL}/api/auth/microsoft`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Exchange failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const token = data.data?.token;
        if (token) {
          cacheBackendToken(token);
          setBackendToken(token);
        }
      })
      .catch(() => {
        // Backend exchange failed - not critical, can retry later
      })
      .finally(() => {
        exchangeStarted.current = false;
        setIsExchanging(false);
      });
  }, [accessToken, backendToken]);

  // Safety net: if MSAL never produces an accessToken, release isExchanging
  useEffect(() => {
    if (!isExchanging) return;
    const timeout = setTimeout(() => setIsExchanging(false), EXCHANGE_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isExchanging]);

  // Returning user with cached token but cold query cache: fire prefetches
  useEffect(() => {
    if (backendToken && !prefetchFired.current) {
      fireDashboardPrefetches(queryClient);
      prefetchFired.current = true;
    }
  }, [backendToken, queryClient]);

  // Fetch user profile once we have a backend token
  useEffect(() => {
    if (!backendToken || userRole !== undefined) return;

    if (!BACKEND_URL) return;

    fetch(`${BACKEND_URL}/api/v1/users/me`, {
      headers: {
        Authorization: `Bearer ${backendToken}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.role) setUserRole(data.role);
        if (data?.display_name) setUserName(data.display_name);
      })
      .catch(() => {
        // Non-critical
      });
  }, [backendToken, userRole]);

  const user: AuthUser | null = useMemo(() => {
    if (testAuth) return testAuth.user;
    // Prefer MSAL account info when available
    if (account) {
      return {
        id: account.localAccountId || account.homeAccountId || "",
        email: account.username || "",
        name: userName || account.name || account.username || "",
        tenantId: account.tenantId || undefined,
        role: userRole,
      };
    }
    // Fallback: derive user from backend JWT claims
    if (backendToken) {
      const jwtUser = parseUserFromJwt(backendToken);
      if (jwtUser) {
        return {
          ...jwtUser,
          name: userName || jwtUser.email,
          role: userRole,
        };
      }
    }
    return null;
  }, [testAuth, account, backendToken, userRole, userName]);

  const login = useCallback(async () => {
    // Redirect to backend OAuth flow which exchanges the auth code with
    // client_secret, yielding a refresh token (MSAL SPA flow cannot).
    window.location.href = `${BACKEND_URL}/api/auth/login`;
  }, []);

  const logout = useCallback(async () => {
    const { localLogout } = await import("@/lib/localLogout");
    await localLogout("/login");
  }, []);

  return {
    isAuthenticated,
    isLoading,
    isExchanging,
    user,
    accessToken,
    backendToken,
    error,
    login,
    logout,
  };
}
